(function () {
  'use strict';

  /* ── Coupon quick-apply ──────────────────────────────────────────────
     Fills the discount input and clicks Apply, letting Kalles' own
     <hdt-cart-discount> element (global.min.js) do the POST to
     /cart/update.js. Two things layered on top of it:

     1. A locked chip must not fire. The Liquid only renders the chip as a
        <button data-apply-coupon> once the cart clears the code's minimum
        subtotal; below it the chip is a <div data-coupon-locked>, which has
        no data-apply-coupon and so never reaches here. The aria-disabled
        guard is belt-and-braces for a future variant that keeps the
        attribute while locked.
     2. A specific failure reason. Kalles shows the generic
        `discount_error_message` for every rejection, so the one failure our
        threshold gate cannot predict - subtotal is high enough but the items
        are all sale products, which CREATE* excludes - read as "your code is
        wrong". The chip carries its own message in data-coupon-error and we
        write it in BEFORE the click; Kalles' failure branch only toggles
        display, it never overwrites textContent, so it survives.
     ------------------------------------------------------------------ */
  var defaultDiscountError = null;
  var lastAttemptedCode = '';

  function setDiscountError(msg) {
    var el = document.querySelector('[data-cart-discount-error]');
    if (!el) return;
    if (defaultDiscountError === null) defaultDiscountError = el.textContent;
    el.textContent = msg || defaultDiscountError;
  }

  /* ── Gift card codes are NOT discount codes ───────────────────────────
     Shopify's Ajax Cart API only understands discounts: /cart/update.js
     {discount: …} is matched against the shop's discount codes and nothing
     else. A gift card is a payment method, not a cart-level discount, and
     Shopify exposes it only through the Storefront API (cartGiftCardCodes)
     and through checkout — neither of which a theme's cart box can reach.
     There is no Ajax endpoint for it at any API version.

     So a gift card typed into this field ALWAYS comes back
     {"discount_codes":[{"code":"…","applicable":false}]} (verified against
     the live cart), and Kalles renders the section's generic rejection
     string — which tells a customer holding a perfectly good gift card that
     their card is invalid. We cannot make the field accept it; we can stop
     it lying about why. Same mechanism as the chip's data-coupon-error: the
     message is written BEFORE the click, and Kalles' failure branch only
     toggles display, so it survives.
     ------------------------------------------------------------------ */
  function looksLikeGiftCard(code) {
    var s = String(code || '').replace(/[^a-z0-9]/gi, '');
    /* Shopify gift card codes are alphanumeric, 8–20 characters, and an
       auto-generated one (which is every card this store sells, since they
       are issued by buying the gift card product) is exactly 16. They are
       commonly pasted in spaced groups of four, which the strip above
       normalises. Discount codes here are short and wordlike (CREATE10 is 8).

       This is a SHAPE check, not a validity check — the code is never
       verified against Shopify, deliberately. See docs/19 §GIFT-1c: a
       storefront endpoint that answers "is this gift card real?" is a
       brute-force oracle against stored value, which is why Shopify does not
       expose one to themes.

       The floor stays at 12 rather than pinning to 16 because the failure
       modes are asymmetric: showing the button for an unusual coupon is
       harmless noise, while hiding it from a real gift card puts the shopper
       straight back into the original bug. The 20-character ceiling is
       Shopify's documented maximum, so anything longer cannot be a gift
       card. */
    return s.length >= 12 && s.length <= 20 && /[a-z]/i.test(s) && /[0-9]/.test(s);
  }

  function giftCardMessageFor(code) {
    if (!looksLikeGiftCard(code)) return null;
    var el = document.querySelector('[data-cart-discount-error]');
    // Falls back to null -> the section's default message if unset.
    return (el && el.getAttribute('data-giftcard-message')) || null;
  }

  var applyingViaChip = false;

  function hdtApplyCoupon(code, chip) {
    if (chip && chip.getAttribute('aria-disabled') === 'true') return;
    var input = document.querySelector('[data-cart-discount-input]');
    var applyBtn = document.querySelector('[data-cart-discount-apply]');
    if (!input || !applyBtn) return;
    setDiscountError(chip && chip.getAttribute('data-coupon-error'));
    input.value = code;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    // .click() dispatches synchronously, so the capture-phase listener below
    // runs inside this call and sees the flag - that is what stops it resetting
    // the chip-specific message we just wrote.
    applyingViaChip = true;
    applyBtn.click();
    applyingViaChip = false;
  }

  /* Kalles blanks the input on a rejected code (`e.value=""` in its failure
     branch), so a customer who mistypes one character loses the whole code and
     has to retype it into a cart drawer on a phone. Capture what was submitted
     before that runs, then put it back when the error appears. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-cart-discount-apply]')) return;
    var input = document.querySelector('[data-cart-discount-input]');
    lastAttemptedCode = input ? input.value : '';
    // A hand-typed code must not inherit the chip's "some kits are excluded"
    // wording from an earlier chip tap - reset to the section's default first,
    // or to the gift-card explanation when the code is shaped like one.
    if (!applyingViaChip) setDiscountError(giftCardMessageFor(lastAttemptedCode));
  }, true); // capture phase - beats Kalles' bubble-phase handler to the value

  var errObserver = null;
  var errNode = null;
  function watchDiscountError() {
    var el = document.querySelector('[data-cart-discount-error]');
    if (!el || el === errNode) return;
    errNode = el;
    defaultDiscountError = null; // re-read the default off the fresh render
    if (errObserver) errObserver.disconnect();
    errObserver = new MutationObserver(function () {
      if (errNode.style.display === 'none' || !lastAttemptedCode) return;
      var input = document.querySelector('[data-cart-discount-input]');
      if (input && !input.value) input.value = lastAttemptedCode;
    });
    errObserver.observe(errNode, { attributes: true, attributeFilter: ['style'] });
  }

  /* ── Cart total accordion ── */
  function toggleCartAccordion() {
    var box = document.getElementById('hdt-cart-total-accordion');
    var details = document.getElementById('hdt-cart-total-details');
    if (box && details) {
      var isOpen = box.classList.toggle('is-open');
      box.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      details.style.display = isOpen ? 'block' : 'none';
    }
  }

  /* ── Confetti burst - reduced-motion safe ── */
  var CONFETTI_COLORS = ['#465ae6', '#ff4683', '#fdda0d', '#388e3c', '#0a0a0a'];
  function burstConfetti(originEl) {
    if (!originEl || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var rect = originEl.getBoundingClientRect();
    var cx = rect.left + rect.width / 2;
    var cy = rect.top + rect.height / 2;
    for (var i = 0; i < 18; i++) {
      (function (idx) {
        var p = document.createElement('div');
        p.className = 'hdt-confetti-particle';
        p.style.cssText = [
          'left:' + (cx + (Math.random() - 0.5) * 80) + 'px',
          'top:' + (cy - 20 + (Math.random() - 0.5) * 20) + 'px',
          'background:' + CONFETTI_COLORS[idx % CONFETTI_COLORS.length],
          'transform:rotate(' + (Math.random() * 360) + 'deg)',
          'animation-delay:' + (Math.random() * 0.25) + 's'
        ].join(';');
        document.body.appendChild(p);
        setTimeout(function () { if (p.parentNode) p.parentNode.removeChild(p); }, 900);
      })(i);
    }
  }

  /* ── Track free-ship unlock across re-renders ── */
  var wasUnlocked = false;
  function checkFreeShipUnlock() {
    var fill = document.querySelector('.hdt-custom-ship-bar-fill');
    var isNowUnlocked = fill && fill.classList.contains('is-complete');
    if (isNowUnlocked && !wasUnlocked) {
      burstConfetti(fill);
    }
    wasUnlocked = !!isNowUnlocked;
  }

  /* ── Cart dialog helpers ── */
  function getCartDialog() {
    return document.getElementById('CartDrawer');
  }

  function getCartDrawerComponent() {
    var dialog = getCartDialog();
    if (!dialog) return null;
    return dialog.closest('hdt-cart-drawer');
  }

  /* Kalles sets html[scroll-lock] whenever a modal dialog with the scroll-lock
     attribute opens, and clears it from DialogComponent.close() once its own
     open-dialog stack is empty. Any close that bypasses that method (a raw
     dialog.close(), a drawer removed from the DOM while open, a third-party
     checkout overlay tearing things down) leaves the attribute set AND leaves a
     stale entry on that stack - so html{overflow:hidden} sticks and no LATER
     dialog can clear it either. Reconcile against the DOM: with no dialog open
     anywhere, nothing can legitimately be holding the lock. */
  function releaseScrollLockIfIdle() {
    if (document.querySelector('dialog[open]')) return;
    document.documentElement.removeAttribute('scroll-lock');
  }

  /* ── Close the cart drawer ──
     Always through the component's own close(), which releases the top layer,
     the scroll lock and the open-dialog stack, and plays the same slide-out
     the X button and the click-outside already use. The old raw
     `dialog.close()` here skipped all of that: after Express Checkout the page
     was left permanently scroll-locked, and every subsequent drawer close was
     locked out of clearing it too. */
  function closeCartDrawer() {
    var dialog = getCartDialog();
    if (!dialog || !dialog.hasAttribute('open')) return;
    /* Idempotent. Kalles' own [aria-controls] handler closes the X button too,
       and whichever of the two document listeners is registered first wins the
       race. A second close() while the first is animating reverses the shared
       transform array again, so the drawer slides back IN mid-exit. */
    if (dialog.classList.contains('dialog-closing')) return;
    var comp = getCartDrawerComponent();
    if (comp && typeof comp.close === 'function') {
      comp.close();
      return;
    }
    // Component not upgraded: mimic the exit, then close and clean up by hand.
    dialog.classList.add('dialog-closing');
    setTimeout(function () {
      try { dialog.close(); } catch (e) { /* already closed */ }
      dialog.classList.remove('dialog-closing');
      releaseScrollLockIfIdle();
    }, 300);
  }

  /* ── Stuck-drawer watchdog ──
     Last line of defence for the failure that made the whole page unclickable:
     a close that starts (.dialog-closing added, drawer animates away) but never
     finishes, leaving an open modal <dialog> in the top layer over a page that
     looks perfectly normal. The known cause is fixed at source in
     kitsters-header.js, but a modal that eats every click is bad enough that it
     should not depend on one call site staying correct - and the same shape can
     come from a third-party checkout overlay unloading mid-animation.

     A legitimate close finishes in ~400ms (Kalles' animateDialogClose), so
     anything still open-and-closing after 1.2s is stuck. This only ever fires
     on an already-broken state; a healthy close clears the timer first. */
  var STUCK_CLOSE_MS = 1200;
  var stuckTimer = null;
  var watchedDialog = null;
  var stateObserver = null;

  function forceCloseStuckDrawer() {
    var dialog = getCartDialog();
    if (!dialog || !dialog.hasAttribute('open') || !dialog.classList.contains('dialog-closing')) return;
    try { dialog.close(); } catch (e) { /* already closed */ }
    dialog.classList.remove('dialog-closing');
    // Keep listeners (custom.js pauses its confetti on this) consistent.
    try { dialog.dispatchEvent(new CustomEvent('dialog:close')); } catch (e) {}
    releaseScrollLockIfIdle();
  }

  function onDrawerStateChange() {
    clearTimeout(stuckTimer);
    stuckTimer = null;
    var dialog = getCartDialog();
    if (!dialog || !dialog.hasAttribute('open')) { releaseScrollLockIfIdle(); return; }
    if (dialog.classList.contains('dialog-closing')) {
      stuckTimer = setTimeout(forceCloseStuckDrawer, STUCK_CLOSE_MS);
    }
  }

  function watchDrawerState() {
    var dialog = getCartDialog();
    if (!dialog || dialog === watchedDialog) return;
    watchedDialog = dialog;
    if (stateObserver) stateObserver.disconnect();
    stateObserver = new MutationObserver(onDrawerStateChange);
    stateObserver.observe(dialog, { attributes: true, attributeFilter: ['class', 'open'] });
    onDrawerStateChange();
  }

  /* Coming back from a checkout popup / redirect is exactly when a leaked lock
     or a half-closed drawer would be discovered by the shopper. Sweep on every
     return path: bfcache restore, tab/app switch back, window refocus. */
  function recoverAfterReturn() {
    forceCloseStuckDrawer();
    releaseScrollLockIfIdle();
  }
  window.addEventListener('pageshow', recoverAfterReturn);
  window.addEventListener('focus', recoverAfterReturn);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) recoverAfterReturn();
  });

  /* ── Delegated handlers (survive HDT AJAX re-renders) ── */
  document.addEventListener('click', function (e) {
    var chip = e.target.closest('[data-apply-coupon]');
    if (chip) { hdtApplyCoupon(chip.getAttribute('data-apply-coupon'), chip); return; }
    var acc = e.target.closest('[data-cart-accordion-toggle]');
    if (acc) { toggleCartAccordion(); return; }

    /* The X button is NOT handled here, deliberately. It carries
       aria-controls="CartDrawer", which Kalles' own document-level handler in
       global.min.js already turns into a proper close. This file used to add a
       second close "belt-and-suspenders" — and because our listener is
       registered first, both ran: one click produced TWO overlapping close()
       calls (measured: `dialog:closing` twice, 1ms apart). Both are async and
       each ends by calling dialog.close() ~400ms later, so the SECOND one shut
       the drawer again after it had been legitimately re-opened — tap the X,
       tap the cart icon, and the drawer opened and then vanished. Kalles owns
       this button; the stuck-drawer watchdog above is the real safety net. */

    // Auto-close cart when Express Checkout is clicked
    var checkoutBtn = e.target.closest('.hdt-btn-checkout-custom, [name="checkout"]');
    if (checkoutBtn && checkoutBtn.closest('#CartDrawer, hdt-cart-drawer')) {
      closeCartDrawer();
      // Do NOT preventDefault - the form submit must proceed to checkout
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var acc = e.target.closest('[data-cart-accordion-toggle]');
    if (acc) { e.preventDefault(); toggleCartAccordion(); }
  });

  /* ── Notification bar: rotating messages ── */
  if (window.hdtCartBarInterval) clearTimeout(window.hdtCartBarInterval);
  if (window.hdtCartBarWait) clearTimeout(window.hdtCartBarWait);
  if (typeof window.hdtBarIndex === 'undefined') window.hdtBarIndex = 0;

  var activeMessages = [];
  var currentTextItem = null;

  // Free shipping is unconditional (no minimum), so these messages take no
  // threshold input. The `data-free-ship-rupees` lookup that used to sit here
  // was already dead - nothing consumed `amt`. Removed 2026-07-28.
  function buildBaseMessages() {
    return [
      '🎉 Join 31,000+ happy makers across India',
      '⭐ Rated 4.8/5 - "super fun!" say our customers',
      '🚚 Free shipping on all orders',
      '💝 Made for you with love and magic',
      '✨ India\'s #1 DIY Experience Brand',
      '🌧️ Rain Alert: Deliveries may take a little longer'
    ];
  }

  function updateMessages() {
    var hiddenMsg = document.getElementById('hdt-hidden-savings-msg');
    var msgs = [];
    if (hiddenMsg && hiddenMsg.textContent.trim().length > 0) {
      msgs.push(hiddenMsg.textContent.trim());
    }
    activeMessages = msgs.concat(buildBaseMessages());
  }

  function runLoop(textItem, barContainer) {
    if (!document.body.contains(textItem)) { window.hdtIsRunning = false; return; }
    window.hdtIsRunning = true;
    window.hdtBarIndex = window.hdtBarIndex % activeMessages.length;
    textItem.innerText = activeMessages[window.hdtBarIndex];
    if (!barContainer.classList.contains('is-visible')) barContainer.classList.add('is-visible');
    textItem.classList.add('hdt-fade-visible');
    window.hdtCartBarWait = setTimeout(function () {
      textItem.classList.remove('hdt-fade-visible');
      window.hdtCartBarInterval = setTimeout(function () {
        window.hdtBarIndex++;
        runLoop(textItem, barContainer);
      }, 500);
    }, 2500);
  }

  function startAnimation() {
    var textItem = document.getElementById('hdt-cart-bar-text');
    var barContainer = document.getElementById('hdt-cart-notification-bar');
    if (!textItem || !barContainer) return;
    if (textItem !== currentTextItem) {
      currentTextItem = textItem;
      if (window.hdtCartBarWait) clearTimeout(window.hdtCartBarWait);
      if (window.hdtCartBarInterval) clearTimeout(window.hdtCartBarInterval);
      updateMessages();
      runLoop(textItem, barContainer);
    } else if (!window.hdtIsRunning) {
      updateMessages();
      runLoop(textItem, barContainer);
    }
  }

  /* ── Sync sticky footer price ── */
  function syncFooterPrice() {
    var hiddenTotal = document.getElementById('hdt-hidden-cart-total');
    var staticFooterPrice = document.getElementById('hdt-static-footer-price');
    if (hiddenTotal && staticFooterPrice) staticFooterPrice.innerHTML = hiddenTotal.innerHTML;
  }

  startAnimation();
  syncFooterPrice();
  checkFreeShipUnlock();
  watchDiscountError();
  watchDrawerState();

  /* ── Watch cart drawer subtree for AJAX updates ── */
  var observerDebounce = null;
  var observer = new MutationObserver(function () {
    clearTimeout(observerDebounce);
    observerDebounce = setTimeout(function () {
      startAnimation();
      syncFooterPrice();
      checkFreeShipUnlock();
      watchDiscountError(); // the node is replaced on every AJAX re-render
      watchDrawerState();   // re-arm if the section (and its <dialog>) was re-rendered
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var bubble = document.getElementById('hdt-cart-count-bubble');
          if (bubble) {
            bubble.innerText = cart.item_count;
            bubble.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
          }
        })
        .catch(function (e) { console.error(e); });
    }, 150);
  });

  var targetNode = document.querySelector('.hdt-dialog-drawer--cart') || document.body;
  observer.observe(targetNode, { childList: true, subtree: true });
})();
